<template>
  <div>
    <div v-if="hasAvailability || editModeActive">
      <div class="flex justify-between items-baseline">
        <p class="text-strong-body">General Availability</p>
        <poppins-button v-if="editModeActive" value="Edit" variant="text" :href="editHref" />
      </div>
    </div>
    <p v-if="showAvailabilityValidUntil" class="text-grey-body text-small">
      Schedule valid until {{ formatInTimeZone(availabilityValidUntil, 'UTC', 'MMMM d, y') }}
    </p>
    <div v-if="!hasAvailability">
      <p v-if="editModeActive" class="text-small text-grey-body mb-16">
        Add your typical schedule to show when you're usually available
      </p>
      <div v-else class="flex flex-col items-center">
        <calendar-up-to-date-illustration />
        <p class="text-grey-body text-small mt-8">Check with {{ sitterName }} to learn more about their schedule</p>
      </div>
    </div>
    <div v-else-if="schedule.length > 0">
      <div v-for="day in daysOfTheWeek" :key="day" class="flex justify-between mt-16 text-body text-grey-header">
        <p>{{ capitalize(day) }}</p>
        <div class="flex flex-col">
          <div v-for="(dayTime, i) in getTimeRanges(day)" :key="`${day}-${i}`" class="text-small mb-8">
            <p v-if="dayTime === 'Unavailable'">Unavailable</p>
            <div v-else>
              {{ formatTimeTo12Hour(dayTime.startTime) }} – {{ formatTimeTo12Hour(dayTime.endTime) }}
              <p v-if="isOvernight(dayTime.startTime, dayTime.endTime)" class="text-right text-caption">(Overnight)</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import capitalize from 'lodash/capitalize';
import { format, parse } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import CalendarUpToDateIllustration from '@/graphics/illustrations/CalendarUpToDateIllustration.vue';
import PoppinsButton from '@/components/button/Button.vue';

export default {
  name: 'GeneralAvailability',
  components: {
    CalendarUpToDateIllustration,
    PoppinsButton,
  },
  props: {
    schedule: {
      type: Array,
      default: () => [],
    },
    sitterName: {
      type: String,
      default: 'the sitter',
    },
    availabilityValidUntil: {
      type: String || null,
      default: null,
    },
    careType: {
      type: String,
      default: '',
    },
    editModeActive: {
      type: Boolean,
      default: false,
    },
  },
  data(): any {
    return {
      daysOfTheWeek: ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'],
    };
  },
  computed: {
    hasAvailability(): boolean {
      return this.schedule.length > 0;
    },
    showAvailabilityValidUntil(): boolean {
      return this.hasAvailability && this.availabilityValidUntil && this.careType !== 'babysitter';
    },
    editHref(): string {
      return `/profile/provider/${this.careType}/${this.careType}_availability/edit?return_to_profile=true`;
    },
  },
  methods: {
    capitalize,
    format,
    formatInTimeZone,
    formatTimeTo12Hour(timeString) {
      const parsedDate = parse(timeString, 'HH:mm', new Date());
      const formattedTime = format(parsedDate, 'h:mm a');
      return formattedTime;
    },
    isOvernight(startTime, endTime) {
      return endTime < startTime;
    },
    getTimeRanges(day) {
      const daySchedule = this.schedule.find((s) => s.day.toUpperCase() === day);
      if (!daySchedule || !daySchedule.timeRanges) {
        return ['Unavailable'];
      }
      return daySchedule.timeRanges;
    },
  },
};
</script>
